import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { playSound, SFX_KEYS } from '../game/systems/SoundManager';

// ============ TYPES ============

interface ClaimCosts {
  tiny: { base: number; upkeep: number };
  small: { base: number; upkeep: number };
  medium: { base: number; upkeep: number };
  large: { base: number; upkeep: number };
  massive: { base: number; upkeep: number };
}

interface IslandData {
  id: string;
  name: string;
  type: 'tiny' | 'small' | 'medium' | 'large' | 'massive';
  biome: string;
  isPermanent: boolean;
  isClaimed: boolean;
  ownerName: string | null;
  ownerType: 'player' | 'crew' | 'faction_regime' | 'faction_navy' | 'faction_pirate' | 'faction_merchant' | 'faction_smuggler' | null;
  customName: string | null;
}

interface TerritoryClaimPanelProps {
  isOpen: boolean;
  onClose: () => void;
  socket: Socket | null;
  island: IslandData | null;
  playerBerries: number;
  playerName: string;
}

// ============ COMPONENT ============

export const TerritoryClaimPanel: React.FC<TerritoryClaimPanelProps> = ({
  isOpen,
  onClose,
  socket,
  island,
  playerBerries,
  playerName,
}) => {
  const [claimCosts, setClaimCosts] = useState<ClaimCosts | null>(null);
  const [customName, setCustomName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleClose = useCallback((): void => {
    playSound(SFX_KEYS.UI_CLOSE);
    onClose();
  }, [onClose]);

  // Fetch claim costs on mount
  useEffect(() => {
    if (socket && isOpen) {
      socket.emit('territory:getCosts');
      
      const handleCosts = (data: { costs: ClaimCosts }): void => {
        setClaimCosts(data.costs);
      };
      
      socket.on('territory:costs', handleCosts);
      
      return () => {
        socket.off('territory:costs', handleCosts);
      };
    }
  }, [socket, isOpen]);

  // Handle socket responses
  useEffect(() => {
    if (!socket) return;

    const handleClaimed = (data: { claim: unknown; cost: number; newBalance: number }): void => {
      setIsLoading(false);
      setSuccess(`Successfully claimed island for ₱${data.cost.toLocaleString()}!`);
      playSound(SFX_KEYS.TERRITORY_CLAIM);
      setTimeout(() => {
        onClose();
      }, 1500);
    };

    const handleError = (data: { message: string }): void => {
      setIsLoading(false);
      setError(data.message);
      playSound(SFX_KEYS.UI_ERROR);
    };

    socket.on('territory:claimed', handleClaimed);
    socket.on('territory:error', handleError);

    return () => {
      socket.off('territory:claimed', handleClaimed);
      socket.off('territory:error', handleError);
    };
  }, [socket, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  // Reset state when panel opens
  useEffect(() => {
    if (isOpen) {
      setCustomName('');
      setError(null);
      setSuccess(null);
      setIsLoading(false);

      playSound(SFX_KEYS.UI_OPEN_TERRITORY_CLAIM);
    }
  }, [isOpen]);

  const handleClaim = useCallback((): void => {
    if (!socket || !island) return;
    
    setIsLoading(true);
    setError(null);

    playSound(SFX_KEYS.UI_CLICK);
    
    socket.emit('territory:claim', {
      islandId: island.id,
      customName: customName.trim() || undefined,
    });
  }, [socket, island, customName]);

  if (!isOpen || !island) return null;

  const cost = claimCosts ? claimCosts[island.type] : null;
  const canAfford = cost ? playerBerries >= cost.base : false;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 4000,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Sometype Mono", monospace',
      }}
    >
      <div
        style={{
          backgroundColor: '#0d1117',
          border: '1px solid #30363d',
          padding: '20px',
          width: '400px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          <span style={{ color: '#ffd700', fontSize: '14px', fontWeight: 'bold' }}>
            CLAIM TERRITORY
          </span>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b949e',
              cursor: 'pointer',
              fontSize: '16px',
              padding: '4px',
            }}
          >
            X
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {/* Island Info */}
          <div
            style={{
              backgroundColor: '#161b22',
              padding: '12px',
              border: '1px solid #30363d',
              marginBottom: '16px',
            }}
          >
            <div style={{ color: '#c9d1d9', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
              {island.name}
            </div>
            <div style={{ color: '#8b949e', fontSize: '11px', textTransform: 'uppercase' }}>
              {island.type} {island.biome} island
            </div>
          </div>

          {/* Claim Status */}
          {island.isPermanent ? (
            <div
              style={{
                backgroundColor: '#2d1b1b',
                padding: '12px',
                border: '1px solid #f85149',
                marginBottom: '16px',
              }}
            >
              <div style={{ color: '#f85149', fontSize: '12px' }}>
                This is a permanent location and cannot be claimed.
              </div>
            </div>
          ) : island.isClaimed ? (
            <div
              style={{
                backgroundColor: '#1c2128',
                padding: '12px',
                border: '1px solid #3fb950',
                marginBottom: '16px',
              }}
            >
              <div style={{ color: '#3fb950', fontSize: '12px' }}>
                Already claimed by <strong>{island.ownerName}</strong>
              </div>
            </div>
          ) : (
            <>
              {/* Cost Info */}
              {cost && (
                <div
                  style={{
                    backgroundColor: '#161b22',
                    padding: '12px',
                    border: '1px solid #30363d',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#8b949e', fontSize: '11px' }}>CLAIM COST</span>
                    <span style={{ color: canAfford ? '#ffd700' : '#f85149', fontSize: '13px', fontWeight: 'bold' }}>
                      ₱{cost.base.toLocaleString()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#8b949e', fontSize: '11px' }}>DAILY UPKEEP</span>
                    <span style={{ color: '#8b949e', fontSize: '12px' }}>
                      ₱{cost.upkeep.toLocaleString()}/day
                    </span>
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid #30363d',
                      marginTop: '8px',
                      paddingTop: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span style={{ color: '#8b949e', fontSize: '11px' }}>YOUR BALANCE</span>
                    <span style={{ color: canAfford ? '#3fb950' : '#f85149', fontSize: '12px' }}>
                      ₱{playerBerries.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Custom Name Input */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#8b949e', fontSize: '11px', display: 'block', marginBottom: '6px' }}>
                  CUSTOM NAME (OPTIONAL)
                </label>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  onKeyDown={(e) => {
                    // Prevent game keybinds from intercepting input
                    e.stopPropagation();
                  }}
                  placeholder={`${playerName}'s Territory`}
                  maxLength={30}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: '#0d1117',
                    border: '1px solid #30363d',
                    color: '#c9d1d9',
                    fontSize: '12px',
                    fontFamily: '"Sometype Mono", monospace',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div
                  style={{
                    backgroundColor: '#2d1b1b',
                    padding: '10px',
                    border: '1px solid #f85149',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ color: '#f85149', fontSize: '11px' }}>{error}</div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div
                  style={{
                    backgroundColor: '#1c2d1c',
                    padding: '10px',
                    border: '1px solid #3fb950',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ color: '#3fb950', fontSize: '11px' }}>{success}</div>
                </div>
              )}

              {/* Claim Button */}
              <button
                onClick={handleClaim}
                disabled={!canAfford || isLoading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: canAfford && !isLoading ? '#238636' : '#21262d',
                  color: canAfford && !isLoading ? '#ffffff' : '#8b949e',
                  border: 'none',
                  cursor: canAfford && !isLoading ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  fontFamily: '"Sometype Mono", monospace',
                }}
              >
                {isLoading ? 'CLAIMING...' : canAfford ? 'CLAIM TERRITORY' : 'INSUFFICIENT FUNDS'}
              </button>

              {/* Benefits Info */}
              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#161b22',
                  border: '1px solid #30363d',
                }}
              >
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                  TERRITORY BENEFITS
                </div>
                <ul style={{ margin: 0, padding: '0 0 0 16px', color: '#c9d1d9', fontSize: '11px' }}>
                  <li style={{ marginBottom: '4px' }}>Safe zone - reduced PvP damage</li>
                  <li style={{ marginBottom: '4px' }}>Resource regeneration bonus</li>
                  <li style={{ marginBottom: '4px' }}>Crew respawn point</li>
                  <li>Customizable permissions</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
